import { useCallback, useEffect, useState } from 'react'
import { META_KEYS, db, metaGet, metaSet } from '../../db/db'
import { useStandalone } from '../../shared/hooks'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export interface InstallPrompt {
  /** Not installed, at least one finished session, and not dismissed before. */
  visible: boolean
  /** The browser offered `beforeinstallprompt`. iOS Safari never does. */
  canPrompt: boolean
  install(): Promise<void>
  dismiss(): Promise<void>
}

/**
 * §10.3: when to show the install nudge, and the browser's install prompt.
 *
 * Eligible after a first completed session rather than on first load — the
 * student has to have a reason to keep the app. A dismissal is remembered in
 * IndexedDB, so the nudge does not come back.
 */
export function useInstallPrompt(): InstallPrompt {
  const standalone = useStandalone()
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null)
  const [eligible, setEligible] = useState(false)
  const [dismissed, setDismissed] = useState(true)

  useEffect(() => {
    if (standalone) return
    let cancelled = false

    function capture(event: Event): void {
      event.preventDefault()
      setDeferred(event as BeforeInstallPromptEvent)
    }

    // iOS Safari has no `beforeinstallprompt` at all, which is why the nudge
    // falls back to explicit Add-to-Home-Screen instructions.
    window.addEventListener('beforeinstallprompt', capture)

    void (async () => {
      const completed = await db.sessions.where('status').anyOf('completed', 'settled').count()
      const already = await metaGet<boolean>(META_KEYS.installNudgeShown)
      if (cancelled) return
      setEligible(completed > 0)
      setDismissed(already === true)
    })()

    return () => {
      cancelled = true
      window.removeEventListener('beforeinstallprompt', capture)
    }
  }, [standalone])

  const install = useCallback(async () => {
    if (!deferred) return
    await deferred.prompt()
    await deferred.userChoice
    setDeferred(null)
  }, [deferred])

  const dismiss = useCallback(async () => {
    await metaSet(META_KEYS.installNudgeShown, true)
    setDismissed(true)
  }, [])

  return { visible: eligible && !dismissed && !standalone, canPrompt: deferred !== null, install, dismiss }
}
